/**
 * Draft case-note API client (0130 — founder rulings 2026-07-06).
 *
 * A draft case note is the server-saved tier between the composer's
 * localStorage autosave (still the in-flight safety net) and a posted
 * note: it travels across devices, lives on the client record's Notes
 * tab under "Your drafts" (author-only), and posts through the NORMAL
 * note path — the composer opens prefilled and the post consumes the
 * draft server-side (draft_id on the note POST).
 *
 *   POST   /api/t/{slug}/v1/clients/{clientId}/note-drafts
 *   GET    /api/t/{slug}/v1/clients/{clientId}/note-drafts
 *   PATCH  /api/t/{slug}/v1/note-drafts/{draftId}
 *   DELETE /api/t/{slug}/v1/note-drafts/{draftId}
 *
 * Mirrors src/lib/notes.ts fetch semantics (ApiError normalization,
 * redirect:'manual' on writes for the X.5.4 auth-expiry signal).
 */

import { writeFetch } from './writeFetch';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from './api';

export interface NoteDraftWire {
  id: string;
  client_id: string;
  author_user_id: string;
  kind: 'note' | 'assessment';
  body: string;
  occurred_on: string; // YYYY-MM-DD
  rate_band: string | null;
  /** Nullable in draft — hours are required at post time, not at save. */
  hours: number | null;
  after_hours: boolean;
  /** 0248: the long-distance tick as the author left it on the draft.
   *  Always set from a server row (toDraftWire); optional for fixtures,
   *  where absent reads as unticked. */
  long_distance_travel?: boolean;
  source: 'composer' | 'assessment_drafter';
  updated_at: string;
  created_at: string;
}

export interface NoteDraftSaveInput {
  kind: 'note' | 'assessment';
  body: string;
  occurred_on: string;
  rate_band: string | null;
  hours: number | null;
  after_hours: boolean;
  /** 0248: the long-distance tick; absent saves as unticked. */
  long_distance_travel?: boolean;
  /** Which door is saving: absent = 'composer'. The drafter's
   *  file-to-record action passes 'assessment_drafter' (the server
   *  dedupes a retried file on content identity). */
  source?: 'composer' | 'assessment_drafter';
}

export interface NoteDraftCreateResult {
  draft: NoteDraftWire;
  /** true = this exact content was already filed (a retried drafter
   *  file action); the existing draft came back, nothing duplicated. */
  already_filed: boolean;
}

// Server row shape (numerics travel as strings; dates as ISO strings).
interface ServerDraftRow {
  id: string;
  client_id: string;
  author_user_id: string;
  kind: 'note' | 'assessment';
  body: string;
  occurred_on: string;
  rate_band: string | null;
  hours: string | null;
  after_hours: boolean;
  /** Optional for older cached shapes (absent reads as unticked). */
  long_distance_travel?: boolean;
  source: 'composer' | 'assessment_drafter';
  updated_at: string;
  created_at: string;
}

function toDraftWire(row: ServerDraftRow): NoteDraftWire {
  return {
    id: row.id,
    client_id: row.client_id,
    author_user_id: row.author_user_id,
    kind: row.kind,
    body: row.body,
    occurred_on: typeof row.occurred_on === 'string' ? row.occurred_on.slice(0, 10) : row.occurred_on,
    rate_band: row.rate_band,
    hours: row.hours === null ? null : Number(row.hours),
    after_hours: row.after_hours,
    long_distance_travel: row.long_distance_travel === true,
    source: row.source,
    updated_at: row.updated_at,
    created_at: row.created_at,
  };
}

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return await res.text().catch(() => null);
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  slug: string,
  path: string,
  body?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(method === 'GET' ? {} : { redirect: 'manual' as const }),
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await parseErrorBody(res),
      `${method} ${path} → HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export const noteDraftsApi = {
  async create(
    slug: string,
    clientId: string,
    input: NoteDraftSaveInput,
    signal?: AbortSignal,
  ): Promise<NoteDraftCreateResult> {
    const res = await request<{ draft: ServerDraftRow; already_filed: boolean }>(
      'POST',
      slug,
      `/clients/${encodeURIComponent(clientId)}/note-drafts`,
      input as unknown as Record<string, unknown>,
      signal,
    );
    return { draft: toDraftWire(res.draft), already_filed: res.already_filed };
  },

  async list(slug: string, clientId: string, signal?: AbortSignal): Promise<NoteDraftWire[]> {
    const res = await request<{ rows: ServerDraftRow[] }>(
      'GET',
      slug,
      `/clients/${encodeURIComponent(clientId)}/note-drafts`,
      undefined,
      signal,
    );
    return res.rows.map(toDraftWire);
  },

  async update(
    slug: string,
    draftId: string,
    patch: Partial<NoteDraftSaveInput>,
    signal?: AbortSignal,
  ): Promise<NoteDraftWire> {
    const { source: _source, ...editable } = patch;
    const res = await request<{ draft: ServerDraftRow }>(
      'PATCH',
      slug,
      `/note-drafts/${encodeURIComponent(draftId)}`,
      editable as unknown as Record<string, unknown>,
      signal,
    );
    return toDraftWire(res.draft);
  },

  async discard(slug: string, draftId: string, signal?: AbortSignal): Promise<void> {
    await request<{ draft_id: string; client_id: string }>(
      'DELETE',
      slug,
      `/note-drafts/${encodeURIComponent(draftId)}`,
      undefined,
      signal,
    );
  },
};

/** Query key for one client's "Your drafts" list (author-scoped server-side). */
export function noteDraftsQueryKey(slug: string, clientId: string): [string, string, string] {
  return ['kith-note-drafts', slug, clientId];
}

/** The record tab's drafts read. Quiet: errors render as nothing. */
export function useNoteDrafts(
  slug: string,
  clientId: string,
  opts: { enabled?: boolean } = {},
): { drafts: NoteDraftWire[]; isLoading: boolean; isError: boolean } {
  const query = useQuery({
    queryKey: noteDraftsQueryKey(slug, clientId),
    queryFn: () => noteDraftsApi.list(slug, clientId),
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
  return {
    drafts: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// ---------------------------------------------------------------------
// The unposted-drafts warning (PR 4b): GET /api/t/{slug}/v1/my-note-drafts
// ---------------------------------------------------------------------

/** One of the caller's own unposted drafts: where and when, never the
 *  text. */
export interface MyDraftSummaryWire {
  id: string;
  client_id: string;
  client_display_name: string;
  occurred_on: string; // YYYY-MM-DD
  /** A client she can no longer open: Discard only. Absent reads as in
   *  scope. */
  out_of_scope?: boolean;
}

/** The caller's own drafts across every client. `count` is uncapped;
 *  `drafts` is the newest 50 by visit date. */
export interface MyDraftSummariesWire {
  count: number;
  drafts: MyDraftSummaryWire[];
}

export function myDraftSummariesQueryKey(slug: string): [string, string] {
  return ['kith-my-note-drafts', slug];
}

/** The warning's read. Refetched on every mount (no staleTime), so a
 *  draft posted on a client's record is gone from the warning the next
 *  time My time or Pre-billing opens. Quiet: an error renders nothing. */
export function useMyDraftSummaries(
  slug: string,
  opts: { enabled?: boolean } = {},
): MyDraftSummariesWire | null {
  const query = useQuery({
    queryKey: myDraftSummariesQueryKey(slug),
    queryFn: ({ signal }) =>
      request<MyDraftSummariesWire>('GET', slug, '/my-note-drafts', undefined, signal),
    enabled: opts.enabled ?? true,
  });
  return query.data ?? null;
}
